<script setup>
  import { ref, computed, watch, watchEffect, onMounted} from 'vue';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import UpgradeSpot from '@/components/UpgradeSpot.vue';
  import BaseModal from './base/BaseModal.vue';

  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const emits = defineEmits(['close']);

  const props = defineProps({
    spots: {
      type: Array,
      required: false
    },
    distanceUnits: {
      type: String,
      default: 'mi'
    },
    groupByDistance: {
      type: Boolean,
      default: false
    },
    show: {
      type: Boolean,
      default: false
    },    
  });

  const closeSpots = ref([]);
  const farSpots = ref([]);
  const showModal = ref(false);

  const totalSpotsCount = computed(() => props.spots.length);
  const totalProSpotsCount = computed(() => props.spots.filter(spot => spot.stations[0].provider === 1 || spot.stations[0].provider === 1370).length)
  const totalPlusSpotsCount = computed(() => props.spots.filter(spot => spot.stations[0].provider !== 1 && spot.stations[0].provider !== 1370).length)
  const closeSpotsDistance = computed(() => props.distanceUnits === 'mi' ? 20 : 32);
  const farSpotsDistance = computed(() => props.distanceUnits === 'mi' ? 65 : 105);
  const mainBlockSpots = computed(() => props.groupByDistance ? closeSpots.value : props.spots)

  const upgradeMessage = computed(() => {
    if (totalProSpotsCount.value > 0 && totalPlusSpotsCount.value > 0) {
      return `Upgrade to unlock<br><span class="jwx-font-weight-700">${totalSpotsCount.value} Plus & Pro Stations</span>`
    }
    else if (totalProSpotsCount.value > 0) {
      return `Upgrade to unlock<br><span class="jwx-font-weight-700">${totalSpotsCount.value} Pro Stations</span>`
    }
    
    return `Upgrade to unlock<br><span class="jwx-font-weight-700">${totalSpotsCount.value} Plus Stations</span>`
  })

  function groupSpotsByDistance(spots = []) {
    closeSpots.value = [];
    farSpots.value = [];

    spots.forEach(spot => {
      if (spot.distance <= closeSpotsDistance.value) {
        closeSpots.value.push(spot);
      }
      else if (spot.distance > closeSpotsDistance.value && spot.distance <= farSpotsDistance.value) {
        farSpots.value.push(spot);
      }
    });
  }

  function closeModal() {
    showModal.value = false;

    emits('close');
  }

  const handleUpgradeClick = () => {
    googleAnalyticsStore.sendEvent('Upgrade_Clicked', {
      Click_Location: 'Wind List Preview Stations',
    });
  }

  onMounted(() => {
    watchEffect(() => showModal.value = props.show);
  });

  watch([() => props.groupByDistance, () => props.spots], () => {
    groupSpotsByDistance(props.spots);
  }, { immediate: true });

</script>

<template>
  <BaseModal
    v-model="showModal"
    @close="closeModal"
    :allowOutsideClick="false"
  >
    <div class="jwx-modal__body" data-scrollbars="false">
      <div class="jwx-modal__upgrade-section">
        <div class="jwx-flex jwx-align-center">
          <p class="jwx-modal__upgrade-text" v-html="upgradeMessage"></p>
          <a href="https://secure.iwindsurf.com/features" target="_blank" class="jwx-button jwx-button--pill jwx-button--ghost jwx-m-left-auto" @click="handleUpgradeClick">Upgrade Now</a>
        </div>
        <div class="jwx-flex jwx-gap-jumbo" style="margin-block-start: var(--jwx-block-spacing-xl); margin-inline: calc(var(--jwx-block-spacing-xl) * -1); padding: var(--jwx-block-spacing-xl); background-color: hsla(0, 0%, 0%, 20%); border-radius: 8px; box-shadow: inset 0 0 10px hsla(0, 0%, 0%, 10%);">
          <div v-if="totalPlusSpotsCount > 0">
            <p class="jwx-font-size-sm jwx-font-weight-700">{{ totalPlusSpotsCount }} Plus Stations</p>
            <p>Plus/Pro/Gold Members</p>
          </div>
          <div v-if="totalProSpotsCount > 0">
            <p class="jwx-font-size-sm jwx-font-weight-700">{{ totalProSpotsCount }} Pro Stations</p>
            <p>Pro/Gold Members</p>
          </div>
        </div>
      </div>
      <div v-if="mainBlockSpots.length > 0" class="jwx-modal__section">
        <div v-if="props.groupByDistance" class="jwx-modal__section-header">
          <h3 class="jwx-modal__section-title">
            Within {{ closeSpotsDistance }} {{ props.distanceUnits }}
          </h3>
        </div>
        <ul class="jwx-block jwx-table-view" data-surface="1">
          <li
            v-for="spot in mainBlockSpots"
            class="jwx-table-view__row"
            :key="spot.spot_id"
          >
            <UpgradeSpot
              :spot="spot"
              :distanceUnits="props.distanceUnits"
            />
          </li>
        </ul>
      </div>
      <div v-if="props.groupByDistance && farSpots.length > 0" class="jwx-modal__section">
        <div class="jwx-modal__section-header">
          <h3 class="jwx-modal__section-title">
            Within {{ farSpotsDistance }} {{ props.distanceUnits }}
          </h3>
        </div>
        <ul class="jwx-block jwx-table-view" data-surface="1">
          <li
            v-for="spot in farSpots"
            class="jwx-table-view__row"
            :key="spot.spot_id"
          >
            <UpgradeSpot
              :spot="spot"
              :distanceUnits="props.distanceUnits"
            />
          </li>
        </ul>
      </div>
    </div>
  </BaseModal>
</template>